import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="container mx-auto px-4 py-16 flex items-center justify-center min-h-screen">
        <div className="auth-card w-full max-w-md p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/assets/lsbp-logo.png" 
              alt="The Lemonade Stand"
              className="mx-auto mb-4"
              style={{ maxWidth: "150px", height: "auto" }}
            />
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Forgot Password?</h1>
            <p className="text-gray-600">
              {submitted 
                ? "Check your email for reset instructions"
                : "Enter your email and we'll send you a reset link"}
            </p>
          </div>

          {submitted ? (
            <div className="text-center">
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-6 rounded-lg mb-6" data-testid="success-message">
                <div className="text-4xl mb-3">📧</div>
                <p className="font-semibold mb-2">Check Your Email!</p>
                <p className="text-sm">
                  If an account exists with <strong>{email}</strong>, you&apos;ll receive a password reset link shortly.
                </p>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-yellow-800 font-semibold mb-2">📋 Demo Note:</p>
                <p className="text-xs text-yellow-700">
                  This is a demo environment. The reset token is logged to the server console instead of being emailed. 
                  Check the backend logs for the reset link.
                </p>
              </div>

              <Link 
                to="/login" 
                className="lemon-button inline-block px-8 py-3 rounded-lg text-white font-bold"
                data-testid="back-to-login-btn"
              >
                Back to Login
              </Link>
            </div>
          ) : (
            <>
              {/* Error Message */}
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="error-message">
                  {error}
                </div>
              )}

              {/* Reset Form */}
              <form onSubmit={handleSubmit} data-testid="forgot-password-form">
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder="your@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="lemon-button w-full py-3 rounded-lg text-white font-bold text-lg disabled:opacity-50"
                  data-testid="submit-btn"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>
            </>
          )}

          {/* Back to Login */}
          {!submitted && (
            <div className="text-center mt-6">
              <Link to="/login" className="text-gray-500 hover:text-gray-700" data-testid="back-to-login-link">
                ← Back to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
